/**
 * Currency Utility - INR (Indian Rupee) Only
 * Provides consistent currency formatting across the application
 */

const CURRENCY = {
  code: 'INR',
  symbol: '₹',
  name: 'Indian Rupee',
  locale: 'en-IN'
};

/**
 * Format a number as Indian Rupee currency
 * @param {number} amount - Amount to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.showSymbol - Show currency symbol (default: true)
 * @param {number} options.minimumFractionDigits - Minimum decimal places (default: 2)
 * @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${CURRENCY.symbol}0.00`;
  }

  const {
    showSymbol = true,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    compact = false
  } = options;

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return `${CURRENCY.symbol}0.00`;
  }

  let formatted;

  if (compact && numAmount >= 100000) {
    // Use compact notation for large numbers (lakhs, crores)
    if (numAmount >= 10000000) {
      // Crores
      const crores = (numAmount / 10000000).toFixed(2);
      formatted = `${crores} Cr`;
    } else if (numAmount >= 100000) {
      // Lakhs
      const lakhs = (numAmount / 100000).toFixed(2);
      formatted = `${lakhs} L`;
    } else {
      formatted = numAmount.toLocaleString(CURRENCY.locale, {
        minimumFractionDigits,
        maximumFractionDigits
      });
    }
  } else {
    formatted = numAmount.toLocaleString(CURRENCY.locale, {
      minimumFractionDigits,
      maximumFractionDigits
    });
  }

  return showSymbol ? `${CURRENCY.symbol}${formatted}` : formatted;
};

/**
 * Format price with currency symbol (standard format)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted price string
 */
export const formatPrice = (amount) => {
  return formatCurrency(amount, { showSymbol: true });
};

/**
 * Format price without currency symbol
 * @param {number} amount - Amount to format
 * @returns {string} Formatted price string without symbol
 */
export const formatPriceWithoutSymbol = (amount) => {
  return formatCurrency(amount, { showSymbol: false });
};

/**
 * Format price in compact notation (for large amounts)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted price in compact notation
 */
export const formatPriceCompact = (amount) => {
  return formatCurrency(amount, { compact: true });
};

/**
 * Format price range
 * @param {number} min - Minimum price
 * @param {number} max - Maximum price
 * @returns {string} Formatted price range
 */
export const formatPriceRange = (min, max) => {
  if (min === max) {
    return formatPrice(min);
  }
  return `${formatPrice(min)} - ${formatPrice(max)}`;
};

/**
 * Format discount amount
 * @param {number} originalPrice - Original price
 * @param {number} discountedPrice - Discounted price
 * @returns {Object} Discount information
 */
export const formatDiscount = (originalPrice, discountedPrice) => {
  if (!originalPrice || !discountedPrice || originalPrice <= discountedPrice) {
    return {
      amount: 0,
      percentage: 0,
      formattedAmount: formatPrice(0),
      formattedPercentage: '0%'
    };
  }

  const discountAmount = originalPrice - discountedPrice;
  const discountPercentage = Math.round((discountAmount / originalPrice) * 100);

  return {
    amount: discountAmount,
    percentage: discountPercentage,
    formattedAmount: formatPrice(discountAmount),
    formattedPercentage: `${discountPercentage}%`
  };
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string (e.g., "₹1,234.56")
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  
  // Remove currency symbol and commas
  const cleaned = currencyString
    .replace(CURRENCY.symbol, '')
    .replace(/,/g, '')
    .trim();
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Get currency symbol
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = () => {
  return CURRENCY.symbol;
};

/**
 * Get currency code
 * @returns {string} Currency code
 */
export const getCurrencyCode = () => {
  return CURRENCY.code;
};

/**
 * Get currency name
 * @returns {string} Currency name
 */
export const getCurrencyName = () => {
  return CURRENCY.name;
};

/**
 * Format amount with Indian number system (lakhs, crores)
 * @param {number} amount - Amount to format
 * @param {boolean} showSymbol - Show currency symbol
 * @returns {string} Formatted string
 */
export const formatIndianNumber = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? `${CURRENCY.symbol}0` : '0';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (numAmount >= 10000000) {
    // Crores
    const crores = (numAmount / 10000000).toFixed(2);
    return showSymbol ? `${CURRENCY.symbol}${crores} Cr` : `${crores} Cr`;
  } else if (numAmount >= 100000) {
    // Lakhs
    const lakhs = (numAmount / 100000).toFixed(2);
    return showSymbol ? `${CURRENCY.symbol}${lakhs} L` : `${lakhs} L`;
  } else {
    return formatCurrency(numAmount, { showSymbol, minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
};

/**
 * Validate currency amount
 * @param {number|string} amount - Amount to validate
 * @returns {boolean} True if valid
 */
export const isValidAmount = (amount) => {
  if (amount === null || amount === undefined) return false;
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return !isNaN(numAmount) && numAmount >= 0;
};

export default {
  formatCurrency,
  formatPrice,
  formatPriceWithoutSymbol,
  formatPriceCompact,
  formatPriceRange,
  formatDiscount,
  parseCurrency,
  getCurrencySymbol,
  getCurrencyCode,
  getCurrencyName,
  formatIndianNumber,
  isValidAmount,
  CURRENCY
};

